import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Panel,
  PanelGrid,
  RankList,
  SimpleTable,
  StatGrid,
  StatTile,
  Tag,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import {
  EMPTY_FORUM_DASHBOARD,
  type ForumDashboardDetailKind,
  type ForumDashboardSummary,
} from "../api/shellApi";
import { formatShekels } from "../api/financeApi";
import { ForumKpiDetailDialog } from "./ForumKpiDetailDialog";
import "./forumDashboard.css";

type Props = {
  dashboard?: ForumDashboardSummary | null;
};

const METHOD_LABELS: Record<string, string> = {
  manual: "ידני",
  bank_transfer: "העברה בנקאית",
};

const STATUS_LABELS: Record<string, string> = {
  recorded: "אושר",
  pending: "ממתין",
  cancelled: "בוטל",
};

function fmtCurrency(agorot: number): string {
  return `${formatShekels(agorot)} ₪`;
}

function downloadCsv(
  filename: string,
  rows: Array<{ name: string; incomeAgorot: number }>,
) {
  const header = "אגף,הכנסות\n";
  const body = rows
    .map((r) => `"${r.name.replace(/"/g, '""')}",${(r.incomeAgorot / 100).toFixed(2)}`)
    .join("\n");
  const blob = new Blob(["\uFEFF" + header + body], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type PaymentRow = {
  id: string;
  fundName: string;
  department: string;
  amountAgorot: number;
  paidAt: string;
  method: string;
  status: string;
};

/** Forum Kupot home dashboard — KPI tiles, income by department, top debts, recent payments. */
export function ForumDashboardPanel({ dashboard }: Props) {
  const data = dashboard ?? EMPTY_FORUM_DASHBOARD;
  const [detailKind, setDetailKind] = useState<ForumDashboardDetailKind | null>(null);
  const navigate = useNavigate();

  const paymentColumns = useMemo(
    () => [
      { key: "fundName", label: "קופה" },
      { key: "department", label: "אגף" },
      {
        key: "amountAgorot",
        label: "סכום",
        align: "end" as const,
        render: (r: PaymentRow) => <b className="forum-dash__amount">{fmtCurrency(r.amountAgorot)}</b>,
      },
      {
        key: "paidAt",
        label: "תאריך",
        render: (r: PaymentRow) => {
          const d = new Date(r.paidAt);
          return Number.isNaN(d.getTime()) ? r.paidAt || "—" : d.toLocaleDateString("he-IL");
        },
      },
      { key: "method", label: "אמצעי", render: (r: PaymentRow) => METHOD_LABELS[r.method] ?? r.method ?? "—" },
      {
        key: "status",
        label: "סטטוס",
        render: (r: PaymentRow) => (
          <Tag tone={r.status === "recorded" ? "success" : r.status === "cancelled" ? "danger" : "warning"}>
            {STATUS_LABELS[r.status] ?? r.status ?? "—"}
          </Tag>
        ),
      },
    ],
    [],
  );

  const paymentRows: PaymentRow[] = useMemo(
    () =>
      data.recentPayments.map((p) => ({
        id: p.id,
        fundName: p.fundName,
        department: p.department ?? "—",
        amountAgorot: p.amountAgorot,
        paidAt: p.paidAt,
        method: p.method,
        status: p.status,
      })),
    [data.recentPayments],
  );

  return (
    <>
      <section className="forum-dash" aria-label="לוח בקרה">
        <StatGrid min={180}>
          <StatTile label="הכנסות (שנתי)" value={fmtCurrency(data.annualIncomeAgorot)} sub="נכנס השנה" icon="trend" tone="success" />
          <StatTile label="הוצאות (שנתי)" value={fmtCurrency(data.annualExpenseAgorot)} sub="יצא השנה" icon="wallet" tone="warning" />
          <StatTile
            label="חובות פתוחים"
            value={fmtCurrency(data.openDebtAgorot)}
            sub={`${data.debtorCustomers} קופות חייבות לפורום`}
            icon="alert"
            tone="danger"
            warn={data.debtorCustomers > 0}
            onClick={() => setDetailKind("open_debts")}
          />
          <StatTile
            label="יתרות זכות"
            value={fmtCurrency(data.creditAgorot)}
            sub={data.creditCustomers > 0 ? `${data.creditCustomers} · הפורום חייב לקופות` : "אין זכויות"}
            icon="coin"
            tone="success"
            onClick={() => setDetailKind("credit_balances")}
          />
          <StatTile label="קופות פעילות" value={data.activeFunds} icon="building" tone="primary" onClick={() => setDetailKind("active_funds")} />
          <StatTile label="מנויים פעילים" value={data.activeSubscriptions} icon="repeat" tone="info" onClick={() => setDetailKind("active_subscriptions")} />
          <StatTile
            label="מנויים פוקעים"
            value={data.expiringSubscriptions}
            sub="ב-30 הימים הקרובים"
            icon="bell"
            tone="warning"
            warn={data.expiringSubscriptions > 0}
            onClick={() => setDetailKind("expiring_subscriptions")}
          />
        </StatGrid>

        <PanelGrid cols={2}>
          <Panel
            title="הכנסות לפי אגף"
            sub={`שנת ${data.year}`}
            icon="chart"
            tone="success"
            actions={
              <Button
                size="sm"
                variant="ghost"
                icon="download"
                onClick={() => downloadCsv(`הכנסות-לפי-אגף-${data.year}.csv`, data.incomeByCategory)}
              >
                ייצוא
              </Button>
            }
          >
            <RankList
              tone="success"
              items={data.incomeByCategory.map((row) => ({ key: row.categoryId, label: row.name, value: row.incomeAgorot }))}
              formatValue={(v) => fmtCurrency(v)}
              empty="אין הכנסות לשנה הנוכחית"
            />
          </Panel>

          <Panel
            title="חובות גבוהים ביותר"
            sub="הקופות הבאות חייבות לפורום"
            icon="alert"
            tone="danger"
            actions={
              <Button size="sm" variant="ghost" onClick={() => navigate("/customers")}>
                צפייה בכל
              </Button>
            }
          >
            <RankList
              tone="danger"
              items={data.topDebts.map((d) => ({
                key: d.customerId,
                label: d.fundName,
                sub: d.department ?? undefined,
                value: d.balanceAgorot,
                onClick: () => navigate(`/customers?card=${d.customerId}`),
              }))}
              formatValue={(v) => fmtCurrency(v)}
              empty="אין חובות פתוחים"
            />
          </Panel>
        </PanelGrid>

        <Panel
          title="תשלומים אחרונים"
          icon="coin"
          tone="primary"
          flush
          actions={
            <Button size="sm" variant="ghost" onClick={() => navigate("/subscriptions")}>
              צפייה בכל
            </Button>
          }
        >
          <SimpleTable columns={paymentColumns} rows={paymentRows} empty="אין תשלומים להצגה" ariaLabel="תשלומים אחרונים" />
        </Panel>
      </section>
      <ForumKpiDetailDialog kind={detailKind} onClose={() => setDetailKind(null)} />
    </>
  );
}

export default ForumDashboardPanel;
